import React, { useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { Link } from 'react-router-dom';
import { BookOpen, BarChart2, MessageCircle, X } from 'lucide-react';
import Loader from '../../../components/common/Loader';
import { motion, AnimatePresence } from 'framer-motion'; // Import AnimatePresence
import ChatRoom from '../../../components/common/ChatRoom';

interface DashboardCard {
  to: string;
  Icon: React.ElementType;
  title: string;
  description: string;
  color: "blue" | "green";
}

const dashboardCards: DashboardCard[] = [
  {
    to: "/dashboard/student/my-courses",
    Icon: BookOpen,
    title: "My Courses",
    description: "View and continue your SAP S/4HANA modules.",
    color: "blue",
  },
  {
    to: "/dashboard/student/progress",
    Icon: BarChart2,
    title: "My Progress",
    description: "Track your module completion and assessments.",
    color: "green",
  },
];

const StudentDashboard: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const [isChatOpen, setIsChatOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex min-h-64 items-center justify-center bg-gray-100">
        <Loader />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <p className="text-center mt-10 text-gray-500">You are not logged in.</p>;
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <header className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
        <h1 className="text-3xl font-bold text-gray-800">
          Welcome, <span className="text-blue-600">{user.name}</span>! 👋
        </h1>
        <p className="text-gray-500 mt-1">
          Ready to continue your learning journey? Let's get started.
        </p>
      </header>
      
      {/* Dashboard Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {dashboardCards.map(({ to, Icon, title, description, color }) => {
          const colorClasses = {
            blue: "bg-blue-100 text-blue-600",
            green: "bg-green-100 text-green-600",
          }[color];

          return (
            <Link
              key={to}
              to={to}
              className="group bg-white p-6 rounded-2xl border border-gray-200 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col items-start gap-4"
            >
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${colorClasses}`}>
                <Icon className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-800 group-hover:text-blue-600 transition-colors">
                  {title}
                </h3>
                <p className="text-sm text-gray-500 mt-1">{description}</p>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Floating Chat Button with enhanced animations and accessibility */}
      <button
        onClick={() => setIsChatOpen(!isChatOpen)}
        className="fixed bottom-6 right-6 z-50 bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition transform hover:scale-110"
        aria-label={isChatOpen ? "Close chat" : "Open chat"}
      >
        <AnimatePresence initial={false}>
          {isChatOpen ? (
            <motion.div key="close" initial={{ rotate: -90, scale: 0 }} animate={{ rotate: 0, scale: 1 }} exit={{ rotate: -90, scale: 0 }} transition={{ duration: 0.2 }}>
              <X size={24} />
            </motion.div>
          ) : (
            <motion.div key="open" initial={{ rotate: 90, scale: 0 }} animate={{ rotate: 0, scale: 1 }} exit={{ rotate: 90, scale: 0 }} transition={{ duration: 0.2 }}>
              <MessageCircle size={24} />
            </motion.div>
          )}
        </AnimatePresence>
      </button>

      {/* ChatRoom Popup with responsive classes and animations */}
      <AnimatePresence>
        {isChatOpen && (
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed bottom-0 right-0 left-0 h-[75vh] 
                       sm:bottom-24 sm:right-6 sm:left-auto sm:h-[60vh] sm:max-h-[500px] 
                       w-full sm:w-[400px] 
                       shadow-2xl rounded-t-2xl sm:rounded-2xl 
                       overflow-hidden border bg-white z-40"
          >
            <ChatRoom roomName="general-student-lobby" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StudentDashboard;
